import { useEffect, useState } from "react";
import { useTranslate } from "react-admin";
import { useQuery } from "react-query";

import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import SummarizeIcon from "@mui/icons-material/Summarize";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowsProp,
} from "@mui/x-data-grid";

import {
  getSwapRequestRecord,
  resendSwapRequest,
  swapRequestRecordResubmitBatch,
} from "../../../api/merchant";
import { CustomPagination } from "../../../components/CustomPagination";
import { Button, Tooltip } from "../../../components/MuiGenerals";
import { NoRowsOverlay } from "../../../components/NoRowsOverlay";
import Prompter from "../../../components/Prompter";
import { useAlerting, usePermission, useTranslation } from "../../../hooks";
import useColumnHide, {
  ColumnHiddenMessage,
  getColumnHideConfig,
} from "../../../hooks/useColumnHide";
import {
  batchIdUrlString,
  dataGridDefaults,
  FeatureCodes,
} from "../../../utils/constant";
import { useGenGridCol } from "../../../utils/HelperComp";
import ResubmitButton from "../../Tools/ReSubmit/components/ResubmitButton";
import { checkIsResubmitBtnDisabled } from "../../Tools/ReSubmit/helper/check-is-resubmit-btn-disabled";
import { TableColumnKey } from "../../Tools/ReSubmit/types/table";
import ResubmitPrompterContent from "./ResubmitPrompterContent";
import { useZusParams } from "./SwapRequestRecord";

const translatePrefix = "swapRequestRecord";

function SwapRequestRecordList(props: {
  listMapping: (which: "key" | "name", array: any[], config?: any) => any[][];
}) {
  const { t, te } = useTranslation(translatePrefix);
  const { listMapping } = props;
  const { hasPermission } = usePermission();
  const zusParams = useZusParams();
  const listRes = useQuery({
    queryKey: [
      "getSwapRequestRecord",
      zusParams.body,
      zusParams.refetchCounter,
    ],
    queryFn: () => getSwapRequestRecord(zusParams.body),
  });
  const { rows = [], count = 0 } = listRes.data || {};
  const content: GridRowsProp = listMapping("key", rows, { t, te });

  const [isResubmitPrompterOpen, setIsResubmitPrompterOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>({});
  const translate = useTranslate();
  const { alerting } = useAlerting();

  const cellButton = (name: string) => (params: any) => {
    const batchId = params?.row?.batch_id;
    const [baseLink, icon] =
      name === "op"
        ? ["/operationRecords", <SummarizeIcon />]
        : ["/operationTransactionRecords", <AttachMoneyIcon />];
    const toNewTab = () =>
      window.open(`${baseLink}?${batchIdUrlString}${batchId}`);
    return (
      <Button onClick={toNewTab} color="secondary" variant="contained">
        {icon}
      </Button>
    );
  };

  const ResubmitButtonCell = ({ params }: { params: any }) => {
    const isBtnDisabled = checkIsResubmitBtnDisabled(
      params.row.rawData?.wallet_operation_batch?.status
    );
    const handleClick = () => {
      setIsResubmitPrompterOpen(true);
      setSelectedRowData(params.row);
    };

    if (
      !hasPermission(FeatureCodes.assetManagement.SwapRequestRecord.Resubmit)
    ) {
      return <></>;
    }

    return (
      <ResubmitButton
        onClick={handleClick}
        isDisabled={isBtnDisabled}
        width="50%"
      />
    );
  };

  const handleResubmitYes = async () => {
    const payload = {
      batch_id: selectedRowData.batch_id,
    };

    const res = await swapRequestRecordResubmitBatch(payload);

    if (res) {
      alerting("success", translate("resubmit.alert.success"));
      listRes.refetch();
    }
  };

  const resendCellButtons = (params: GridRenderCellParams) => {
    return (
      <>
        <ResubmitButtonCell params={params} />
        <ActionButton {...{ params }} />
      </>
    );
  };

  const columns: GridColDef[] = [
    useGenGridCol("0", t("operation"), {
      minWidth: 180,
      renderCell: resendCellButtons,
    }),
    useGenGridCol("batch_id", t("batch_id")),
    useGenGridCol("chain_name", t("chain_name")),
    useGenGridCol("smart_account_type", t("smart_account_type")),
    useGenGridCol("wallet_address", t("wallet_address")),
    useGenGridCol("status", t("status")),
    useGenGridCol("max_slippage", t("max_slippage")),
    // useGenGridCol("swap_path", t("swap_path")),
    useGenGridCol("swap_execution_type", t("swap_execution_type")),
    useGenGridCol("currency_from", t("currency_from")),
    useGenGridCol("total_amount_from", t("total_amount_from"), {
      minWidth: 130,
    }),
    useGenGridCol("actual_amount_from", t("actual_amount_from"), {
      minWidth: 130,
    }),
    useGenGridCol("currency_to", t("currency_to")),
    useGenGridCol("total_amount_to", t("total_amount_to"), {
      minWidth: 130,
    }),
    useGenGridCol("actual_amount_to", t("actual_amount_to"), {
      minWidth: 130,
    }),
    // useGenGridCol("actual_amount", t("actual_amount")),
    useGenGridCol("total_count", t("total_count")),
    // useGenGridCol("exchange_rate", t("exchange_rate")),
    // useGenGridCol("estimate_get_amount", t("estimate_get_amount")),
    // useGenGridCol("minimum_get_amount", t("minimum_get_amount")),
    // useGenGridCol("exact_get_amount", t("exact_get_amount")),
    // useGenGridCol("estimate_pay_amount", t("estimate_pay_amount")),
    // useGenGridCol("maximum_pay_amount", t("maximum_pay_amount")),
    // useGenGridCol("exact_pay_amount", t("exact_pay_amount")),
    // useGenGridCol("quoted_time", t("quoted_time")),
    useGenGridCol("created_by", t("created_by"), {}),
    useGenGridCol("creation_time", t("creation_time")),
    useGenGridCol("last_modified_time", t("last_modified_time")),
    useGenGridCol("1", t("view_operation"), {
      renderCell: cellButton("op"),
    }),
    useGenGridCol("2", t("view_operation_transaction"), {
      renderCell: cellButton("opt"),
      minWidth: 170,
    }),
  ].filter((col) => {
    if (
      col.field === "0" &&
      !hasPermission(FeatureCodes.assetManagement.SwapRequestRecord.Resubmit) &&
      !hasPermission(FeatureCodes.assetManagement.SwapRequestRecord.Resend)
    )
      return false;
    if (
      col.field === "1" &&
      !hasPermission(FeatureCodes.assetManagement.OperationRecords.prefix)
    )
      return false;
    if (
      col.field === "2" &&
      !hasPermission(
        FeatureCodes.assetManagement.OperationTransactionRecords.prefix
      )
    ) {
      return false;
    }
    return true;
  }) as GridColDef[];

  useEffect(() => () => zusParams.clear(), []);
  const { colsShown, setColsShown, hasColHidden, localeText } = useColumnHide(
    columns,
    getColumnHideConfig()
  );
  return (
    <>
      <Prompter
        title={translate("resubmit.prompter.title")}
        open={isResubmitPrompterOpen}
        onClose={() => setIsResubmitPrompterOpen(false)}
        content={
          <ResubmitPrompterContent
            batch_id={selectedRowData.batch_id}
            swap_execution_type={selectedRowData.swap_execution_type}
            chain_name={selectedRowData.chain_name}
            currency_from={selectedRowData.currency_from}
            currency_to={selectedRowData.currency_to}
            total_amount={selectedRowData[TableColumnKey.TotalAmount]}
          />
        }
        onYes={handleResubmitYes}
      />
      <ColumnHiddenMessage hasColHidden={hasColHidden} />
      <DataGrid
        {...dataGridDefaults}
        localeText={localeText}
        columnVisibilityModel={colsShown}
        onColumnVisibilityModelChange={setColsShown}
        rows={content}
        columns={columns}
        rowCount={count}
        onPageChange={zusParams.setPage}
        page={zusParams.body.page}
        components={{
          NoRowsOverlay,
          Footer: CustomPagination,
        }}
        componentsProps={{
          footer: { totalRecords: count },
        }}
      />
    </>
  );
}

const ActionButton = (props: any) => {
  const [hasSendArray, setHasSendArray] = useState<Array<number>>([]);
  const { hasPermission } = usePermission();
  const [isApiCallOnGoing, setIsApiCallOnGoing] = useState(false);
  const { alerting } = useAlerting();
  const { params } = props;
  const { t } = useTranslation(translatePrefix);
  let batch_id = params.row.batch_id;

  const handleResend = async () => {
    if (isApiCallOnGoing) {
      return;
    }
    setIsApiCallOnGoing(true);
    const res = await resendSwapRequest({ batch_id });
    if (res) {
      setHasSendArray((pre) => [...pre, batch_id]);
      alerting("success", t("resend_success"));
    }
    setIsApiCallOnGoing(false);
  };

  if (!hasPermission(FeatureCodes.assetManagement.SwapRequestRecord.Resend)) {
    return <></>;
  }

  return (
    <>
      <Tooltip title={t("resend")}>
        <Button
          sx={{
            borderRadius: "18px",
            backgroundColor: "#4a90f7",
            height: "65%",
            width: "50%",
            marginLeft: "12px",
          }}
          variant="contained"
          onClick={handleResend}
          disabled={hasSendArray.includes(batch_id)}
        >
          {t("resend")}
        </Button>
      </Tooltip>
    </>
  );
};

export default SwapRequestRecordList;
